import { gql, useMutation } from '@apollo/client';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';
import { pipe } from 'remeda';

import { Server } from '../apollo/types';
import { formValuesToFormResponse, trimUnusedFormValues, validateForm } from '../components/forms/question/utils';
import {
  FormSubmissionCallbackResponse,
  FormSubmissionMutationData,
  FormSubmissionMutationVariables,
  FormSubmissionStatusCode,
  GroupedFormData,
} from '../components/forms/types';
import { combinedFormToQuestions } from '../components/forms/utils';
import { logger } from '../datadog';
import { visitorAtom, visitorFormValues } from './state';

export const SUBMIT_CLIENT_FORM_GQL = gql`
  mutation SubmitFormResponse(
    $customerId: String!
    $formResponse: String!
    $formShortId: String!
    $isLastForm: Boolean!
    $startedAt: DateTime!
    $completedBy: CompletedBy
  ) {
    submitFormResponse(
      values: {
        customerId: $customerId
        formResponse: $formResponse
        formShortId: $formShortId
        isLastForm: $isLastForm
        startedAt: $startedAt
        completedBy: $completedBy
      }
    ) {
      formSubmission {
        formId
        formResponseId: id
      }
    }
  }
`;

export function useSubmitClientFormMutation() {
  return useMutation<FormSubmissionMutationData, FormSubmissionMutationVariables>(SUBMIT_CLIENT_FORM_GQL, {
    context: {
      server: Server.MONOLITH,
      skipAuth: true,
    },
  });
}

export function useSubmitClientFormCallback() {
  const [submitFormMutation] = useSubmitClientFormMutation();

  const getFormValuesByTitle = useAtomCallback(useCallback((get) => get(visitorFormValues), []));
  const { customerId } = useAtomValue(visitorAtom);

  return useCallback(
    async ({
      pendingForm,
      isLastForm,
      startedAt,
      completedBy,
    }: {
      pendingForm: GroupedFormData;
      startedAt: string;
      isLastForm?: boolean;
      completedBy?: 'CLIENT';
    }): Promise<FormSubmissionCallbackResponse> => {
      const formValuesByTitle = getFormValuesByTitle();
      const questions = combinedFormToQuestions(pendingForm);
      const { formShortId, formTitle } = pendingForm;
      const formValues = formValuesByTitle && formValuesByTitle[formTitle];

      const validationErrors = validateForm(questions, formValues);
      if (!formValues) {
        return {
          status: 'failure',
          code: FormSubmissionStatusCode.MISSING_FORM_VALUES,
          value: null,
        };
      }

      if (!formShortId) {
        logger.error('Missing form short ID when submitting form');
        return {
          status: 'failure',
          code: FormSubmissionStatusCode.MISSING_REQUIRED_FIELDS,
          value: new Set(['formShortId']),
        };
      }

      if (validationErrors.size) {
        return {
          status: 'failure',
          code: FormSubmissionStatusCode.VALIDATION_ERROR,
          value: validationErrors,
        };
      }

      try {
        const response = await submitFormMutation({
          variables: {
            customerId: customerId!,
            formShortId,
            formResponse: pipe(
              formValues,
              (formValues) =>
                trimUnusedFormValues(
                  formValues,
                  questions.map((q) => q.questionElement!),
                ),
              formValuesToFormResponse,
              JSON.stringify,
            ),
            isLastForm: !!isLastForm,
            startedAt,
            completedBy,
          },
        });
        return {
          status: 'success',
          code: FormSubmissionStatusCode.SUCCESS,
          value: response.data?.submitFormResponse?.formSubmission,
        };
      } catch (e) {
        const error = e as Error;
        logger.error('Error while submitting form (visitor client form)', {}, error);
        return {
          status: 'failure',
          code: FormSubmissionStatusCode.ERROR,
          value: error,
        };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}
